import { getValidConcessionAmount } from "../concessionDetails";
import { sanitizeConcessionData } from "./common";

export function getFeeCategoryByItemType(FEE_CONFIG, itemType) {
    // Loop through each category in the configuration object.
    for (const [category, configData] of Object.entries(FEE_CONFIG)) {
        // Check if the list of itemTypes for this category includes the one we're looking for.
        if (configData.itemTypes.includes(itemType)) {
            return category; // Return the matching category name.
        }
    }
    
    // If no match is found after checking all categories, return a default value.
    return 'MISCELLANEOUS';
}

export function generateFeeConfigFromApi(apiData) {
  const categoryRules = {
    REGISTRATION_FEE: ["registration fee"],
    ADMISSION_KIT: ["admission kit", "adm fee"],
    TUITION_FEE: ["tuition fee", "dlp course", "tutionfee", "tition fee"],
    TECH_EXAM_FEE: ["tech + exam"],
    TECHNOLOGY_FEE: ["technology fee"],
    EXAM_FEE: ["exam fee"],
    CLASSROOM_FEE: ["classroom service"],
    TRANSPORT_FEE: ["transport fee"],
    PROSPECTUS_FEE: ["prospectus fees"],
  };

  const generatedConfig = {};

  apiData.forEach((item) => {
    const descriptionLower = item.description.toLowerCase();
    let foundCategory = "MISCELLANEOUS";

    for (const [category, keywords] of Object.entries(categoryRules)) {
      if (keywords.some((keyword) => descriptionLower.includes(keyword))) {
        foundCategory = category;
        break;
      }
    }

    if (!generatedConfig[foundCategory]) {
      generatedConfig[foundCategory] = {
        description: foundCategory.replace(/_/g, " "),
        itemTypes: [],
      };
    }
    generatedConfig[foundCategory].itemTypes.push(item.itemTypeId);
  });

  return generatedConfig;
}

export const getAppliedConcessions = async (
  FEE_CONFIG,
  concessionDetails,
  totalAmt,
  studentCurrentYear
) => {
  // This map links our categories to the property names in the concession items.
  const FEE_CONCESSION_FIELD_MAP = {
    REGISTRATION_FEE: "regFeeConcession",
    ADMISSION_KIT: "adminFeeConcession",
    TUITION_FEE: "tutionFeeConcession",
    TECH_EXAM_FEE: "techFeeConcession",
    CLASSROOM_FEE: "classFeeConcession",
  };

  // NEW: This map links our categories to the identifiers in the 'extraFeeComponents' string.
  const FEE_EXTRA_FEE_MAP = {
    REGISTRATION_FEE: "REG_FEE",
    ADMISSION_KIT: "ADMIN_FEE",
    TUITION_FEE: "TUITION_FEE",
    TECH_EXAM_FEE: "TECH_FEE", // Or handle as needed
    CLASSROOM_FEE: "CLASS_FEE",
  };

  // Dynamically initialize data structures
  const initialConcessionObject = {};
  const initialAppliedList = {};
  
  Object.keys(FEE_CONFIG).forEach((category) => {
    initialConcessionObject[category] = {
      flAmt: 0,
      ylAmt: 0,
      ulAmt: 0,
      oAmt: 0,
    };
    initialAppliedList[category] = [];
  });

  let totalConsession = { ...initialConcessionObject };
  let totalPendingConsession = { ...initialConcessionObject };
  let concessionAppliedList = { ...initialAppliedList };

  if (!Array.isArray(concessionDetails) || concessionDetails.length === 0) {
    return {
      totalPendingConsession,
      totalConsession: sanitizeConcessionData(totalConsession),
      concessionAppliedList,
    };
  }
  const isApprovalRequired = !!(
    concessionDetails.find((item) => item.concessionType === "CUSTOM") ||
    concessionDetails?.[0]?.extraFeeComponents
  );

  console.log(concessionDetails.find((item) => item.concessionType === "CUSTOM") ,concessionDetails?.[0]?.extraFeeComponents)

  const isRejected = ["L1_REJECTED", "L2_REJECTED", "L3_REJECTED"].includes(
    concessionDetails?.[0]?.concessionApprovalStatus
  );

  const isApproved = concessionDetails?.[0]?.extraFeeComponents
    ? concessionDetails?.[0]?.isApproved
    : concessionDetails.find((item) => item.concessionType === "CUSTOM")
        ?.isApproved;
  concessionDetails.forEach((item) => {
    const lumpsumYear =
      item?.concessionMethodType === "LUMPSUM_First_Year"
        ? studentCurrentYear
        : item?.concessionMethodType === "LUMPSUM_Year_On_Year"
        ? 0 ?? null
        : item?.concessionMethodType === "LUMPSUM_Full_Course"
        ? 0
        : null;

    const key =
      item?.concessionMethodType === "LUMPSUM_First_Year"
        ? "flAmt"
        : item?.concessionMethodType === "LUMPSUM_Year_On_Year"
        ? "ulAmt"
        : item?.concessionMethodType === "LUMPSUM_Full_Course"
        ? "ulAmt"
        : "oAmt";
 
    // Logic for APPROVED concessions (or those not requiring approval)
    if ((isApprovalRequired && isApproved) || !isApprovalRequired) {
      for (const category in FEE_CONCESSION_FIELD_MAP) {
        const concessionField = FEE_CONCESSION_FIELD_MAP[category];
        const concessionValue = Number(item[concessionField] || 0);

        if (concessionValue > 0) {
          if (item.concessionParameter === "PERCENTAGE") {
            totalConsession[category][key] += concessionValue;
          } else if (item.concessionParameter === "AMOUNT") {
            // Bridge to old data structure for total amounts
            const legacyKey = Object.keys(totalAmt).find((k) =>
              FEE_CONFIG[category]?.itemTypes[0]?.includes(k)
            );
            const feeTotalAmt = totalAmt?.[legacyKey] ?? 0;
            if (feeTotalAmt > 0) {
              const amountAsPercent =
                (getValidConcessionAmount(concessionValue, feeTotalAmt) /
                  feeTotalAmt) *
                100;
              totalConsession[category][key] += amountAsPercent;
            }
          }
          concessionAppliedList[category].push({
            id: item.id,
            constrain: lumpsumYear,
          });
        }
      }
    }

   if (isApprovalRequired && !isRejected && !isApproved) {
      const extraFeeArr = item?.extraFeeComponents?.split(",") || [];

      for (const category in FEE_CONCESSION_FIELD_MAP) {
        const concessionField = FEE_CONCESSION_FIELD_MAP[category];
        const concessionValue = Number(item[concessionField] || 0);
        if (concessionValue === 0) continue;

        let pendingValue = 0;
        let approvedValue = 0;

        // Calculate the value (as a percentage)
        let valueAsPercent = 0;
        if (item.concessionParameter === "PERCENTAGE") {
          valueAsPercent = concessionValue;
        } else if (item.concessionParameter === "AMOUNT") {
          const legacyKey = Object.keys(totalAmt).find((k) =>
            FEE_CONFIG[category]?.itemTypes[0]?.includes(k)
          );
          const feeTotalAmt = totalAmt?.[legacyKey] ?? 0;
          if (feeTotalAmt > 0) {
            valueAsPercent =
              (getValidConcessionAmount(concessionValue, feeTotalAmt) /
                feeTotalAmt) *
              100;
          }
        }

        if (item.concessionType === "DEFINED") {
          const extraFeeIdentifier = FEE_EXTRA_FEE_MAP[category];
          if (extraFeeArr.includes(extraFeeIdentifier)) {
            pendingValue = valueAsPercent; // This portion is pending
          } else {
            approvedValue = valueAsPercent; // This portion is already approved
          }
        } else if (item.concessionType === "CUSTOM") {
          pendingValue = valueAsPercent; // All custom concessions are pending
        }

        // Apply the calculated values
        if (pendingValue > 0) {
          totalPendingConsession[category][key] += pendingValue;
        }
        if (approvedValue > 0) {
          totalConsession[category][key] += approvedValue;
          concessionAppliedList[category].push({
            id: item.id,
            constrain: lumpsumYear,
          });
        }
      }
    }
  });

  return {
    totalPendingConsession,
    totalConsession: sanitizeConcessionData(totalConsession),
    concessionAppliedList,
  };
};


/**
 * Processes raw fee breakup data, groups it by installment, and calculates totals.
 * This function is fully refactored to use the dynamic fee configuration.
 *
 * @param {Object} feeBreakupData - The raw fee data from the API.
 * @param {Object} itemTypes - The master list of item type definitions from the API.
 * @returns {Object} An object containing groupedData, itemTypeLabelMapping, and totals.
 */
export const createFeeAmountMapping = async (feeBreakupData, itemTypes) => {
  if (!Array.isArray(feeBreakupData?.data) || !Array.isArray(itemTypes?.data)) {
    return {}; // Return empty if data is invalid
  }

  // Step 1: Generate the configuration from the master item types list.
  const config = generateFeeConfigFromApi(itemTypes.data);

  const totalsByCategory = {};
  const uniqueCategories = new Set();

  // Step 2: Create a performance-optimized map for descriptions.
  // This avoids using a slow .find() inside the later .map() loop.
  const descriptionMap = new Map(
    itemTypes.data.map((item) => [item.itemTypeId, item.description])
  );

  // Step 3: Process the fee data to calculate totals using the reliable category.
  feeBreakupData.data.forEach((element) => {
    // REPLACED: const elementType = element?.itemType?.slice(-2);
    const feeCategory = getFeeCategoryByItemType(config, element.itemType);

    // Build totals with meaningful keys (e.g., 'TUITION_FEE')
    totalsByCategory[feeCategory] =
      (totalsByCategory[feeCategory] ?? 0) + element.baseAmount;
    uniqueCategories.add(feeCategory);
  });

  // Step 4: Create the label mapping directly from our generated config.
  const itemTypeLabelMapping = {};
  uniqueCategories.forEach((category) => {
    // The config already has the clean description.
    itemTypeLabelMapping[category] = config[category]?.description || category;
  });

  // Step 5: Enrich and group the data by installment number.
  const tempData = feeBreakupData.data.map((item) => ({
    ...item,
    // Use the fast description map from Step 2.
    desc: descriptionMap.get(item.itemType) || item.desc || "Unknown Fee",
    // This logic for installment number remains, as it serves a different purpose.
    installmentNo: item.itemType?.slice(-4, -2),
  }));

  const groupedData = tempData.reduce((accumulator, currentItem) => {
    const key = currentItem.installmentNo;
    (accumulator[key] = accumulator[key] || []).push(currentItem);
    return accumulator;
  }, {});

  return { groupedData, itemTypeLabelMapping, total: totalsByCategory, config };
};


export const calculateConcessionPercentage = (breakup) => {
  // debugger
  // Validate input
  if (!breakup || typeof breakup !== "object") return {};

  const componentItemsList = Object.values(breakup);

  if (!componentItemsList || componentItemsList.length === 0) {
    return {};
  }

  // Flatten all items
  const componentItems = componentItemsList.flat();

  if (!componentItems || componentItems.length === 0) return {};

  const componentsMap = {};

  componentItems.forEach((item) => {
    if (!item?.itemType) return;

    const key = item.itemType.slice(-2); // last 2 digits
    if (!key) return;

    // Ensure numeric values
    const baseAmount = Number(item.baseAmount) || 0;
    const concession = Number(item.consession || item.concession) || 0;

    if (componentsMap[key]) {
      const current = componentsMap[key];
      componentsMap[key] = {
        baseAmount: current.baseAmount + baseAmount,
        concession: current.concession + concession,
      };
    } else {
      componentsMap[key] = {
        baseAmount,
        concession,
      };
    }
  });

  // Calculate concession percentage safely
  const mapWithConcessionPercentage = {};
  Object.entries(componentsMap).forEach(([key, value]) => {
    mapWithConcessionPercentage[key] = {
      ...value,
      concessionPercentage: value.baseAmount
        ? (value.concession / value.baseAmount) * 100
        : 0,
    };
  });

  return mapWithConcessionPercentage;
};


export const getPendingConcessionPercentageMap = (breakup) => {
  if (!breakup || typeof breakup !== "object") return {};

  const componentsMap = {}; // { componentKey: { baseAmount, pendingConcession } }

  Object.values(breakup).forEach((installmentArray) => {
    installmentArray.forEach((item) => {
      if (!item?.itemType) return;

      const key = item.itemType.slice(-2); // last 2 digits as component key
      const baseAmount = Number(item.baseAmount) || 0;
      const pending = Number(item.concApprovalPending) || 0;

      if (componentsMap[key]) {
        componentsMap[key].baseAmount += baseAmount;
        componentsMap[key].pending += pending;
      } else {
        componentsMap[key] = {
          baseAmount,
          pending,
        };
      }
    });
  });

  // Calculate pending % for each component
  const pendingPercentMap = {};
  Object.keys(componentsMap).forEach((key) => {
    const { baseAmount, pending } = componentsMap[key];
    pendingPercentMap[key] =
      baseAmount > 0 ? ((pending / baseAmount) * 100).toFixed(2) : "0.00";
  });

  return pendingPercentMap;
};
